import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBranding } from '../context/BrandingContext';
import { Building2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OWNER = 'Francesco Cipolla';
const CONTACT = 'info@elevenda.com';
const UPDATED = '29 ottobre 2026';

const Section = ({ id, title, children }) => (
  <section className="scroll-mt-24" id={id} data-testid={`privacy-section-${id}`}>
    <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{children}</div>
  </section>
);

const PrivacyPage = () => {
  const { branding } = useBranding();
  const brand = branding?.brand_name || 'Elevenda';
  const logo = branding?.logo_url ? `${API}${branding.logo_url}` : null;

  useEffect(() => {
    if (window.hideAppSplash) window.hideAppSplash();
    document.title = `Informativa privacy · ${brand}`;
  }, [brand]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="privacy-page">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5 sm:px-8">
          <Link to="/login" className="flex min-w-0 items-center gap-2.5" data-testid="privacy-brand-link">
            {logo
              ? <img src={logo} alt={brand} className="h-8 w-8 shrink-0 rounded-lg object-contain" />
              : <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Building2 className="h-4 w-4" /></span>}
            <span className="truncate text-lg font-semibold tracking-tight">{brand}</span>
          </Link>
          <Link to="/login" className="inline-flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="privacy-back-link">
            <ArrowLeft className="h-3.5 w-3.5" />
            Vai all’app
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-14 sm:px-8 sm:py-20">
        <p className="text-sm font-medium text-primary" data-testid="privacy-updated">Aggiornata al {UPDATED}</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="privacy-title">
          Informativa privacy
        </h1>
        <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg" data-testid="privacy-intro">
          Questa informativa spiega quali dati raccoglie {brand}, la piattaforma di gestione per saloni,
          perché li raccoglie e come vengono trattati, inclusi i dati a cui accediamo tramite le API di
          Google quando colleghi la tua scheda Google Business.
        </p>

        <div className="mt-12 space-y-12">
          <Section id="titolare" title="1. Titolare del trattamento">
            <p>
              Il titolare del trattamento è <strong className="text-foreground">{OWNER}</strong>, gestore
              della piattaforma {brand}. Per qualsiasi richiesta relativa ai tuoi dati puoi scrivere a{' '}
              <a href={`mailto:${CONTACT}`} className="font-medium text-primary hover:underline" data-testid="privacy-contact-email">{CONTACT}</a>.
            </p>
          </Section>

          <Section id="dati" title="2. Quali dati trattiamo">
            <p>Usando la piattaforma trattiamo:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li><strong className="text-foreground">Dati dell’account:</strong> nome, email, password cifrata, ruolo, dispositivi riconosciuti per la verifica di accesso.</li>
              <li><strong className="text-foreground">Dati dell’attività:</strong> nome del salone, indirizzo, orari, servizi, listino, operatori.</li>
              <li><strong className="text-foreground">Dati dei clienti del salone:</strong> nome, contatti, appuntamenti, storico visite, note e preferenze, inseriti dal salone che resta responsabile di quanto registra.</li>
              <li><strong className="text-foreground">Dati tecnici:</strong> log di accesso e di invio dei messaggi, necessari per sicurezza e assistenza.</li>
            </ul>
          </Section>

          <Section id="google" title="3. Dati ottenuti tramite Google">
            <p>
              Se scegli di collegare il tuo account Google, ti chiediamo l’autorizzazione
              <span className="font-mono text-xs"> https://www.googleapis.com/auth/business.manage</span> e
              i dati di base del profilo (nome ed email) per identificare l’account collegato.
            </p>
            <p>Con quell’autorizzazione accediamo esclusivamente a:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>l’elenco delle schede attività che amministri, per farti scegliere quale collegare al salone;</li>
              <li>informazioni della scheda (nome, indirizzo, orari, contatti, link alle recensioni), per mostrarle e aggiornarle su tua richiesta;</li>
              <li>le recensioni ricevute, per mostrartele e permetterti di rispondere dalla piattaforma;</li>
              <li>la pubblicazione di post sulla scheda, solo quando sei tu a chiederlo.</li>
            </ul>
            <p className="rounded-xl border border-border bg-card p-4 text-foreground">
              <strong>Uso limitato (Limited Use).</strong> L’uso delle informazioni ricevute dalle API di
              Google da parte di {brand} rispetta la{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline" data-testid="privacy-google-policy-link">
                Google API Services User Data Policy
              </a>, compresi i requisiti di Uso Limitato. Non vendiamo questi dati, non li usiamo per
              pubblicità o profilazione, non li cediamo a terzi se non per erogare il servizio che hai
              richiesto, e non li leggiamo con persone se non con il tuo consenso esplicito, per
              assistenza su tua richiesta, per motivi di sicurezza o per obblighi di legge.
            </p>
            <p>
              Puoi revocare il collegamento in qualsiasi momento dalle impostazioni della piattaforma
              oppure dalla pagina{' '}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline" data-testid="privacy-google-revoke-link">
                Account Google · App con accesso
              </a>. Alla revoca eliminiamo i token di accesso conservati.
            </p>
          </Section>

          <Section id="finalita" title="4. Perché trattiamo i dati">
            <p>
              Trattiamo i dati per erogare il servizio richiesto: gestione di agenda e prenotazioni,
              anagrafica clienti, invio di promemoria e comunicazioni per conto del salone, gestione
              della scheda Google Business e delle recensioni, sicurezza degli accessi e assistenza.
              La base giuridica è l’esecuzione del contratto con il salone e, dove previsto, il
              legittimo interesse alla sicurezza della piattaforma.
            </p>
          </Section>

          <Section id="condivisione" title="5. Con chi li condividiamo">
            <p>
              Non vendiamo dati. Li condividiamo solo con i fornitori che rendono possibile il servizio,
              nel limite di quanto serve: Google (scheda attività e recensioni), i servizi di invio di
              email, SMS e messaggi WhatsApp, il provider di pagamento per gli abbonamenti e
              l’infrastruttura cloud che ospita la piattaforma.
            </p>
          </Section>

          <Section id="conservazione" title="6. Per quanto tempo">
            <p>
              Conserviamo i dati finché l’account è attivo. Alla chiusura dell’account i dati vengono
              eliminati entro 30 giorni, salvo quanto debba essere conservato per obblighi fiscali o di
              legge. I codici di verifica e i token di reset password scadono entro pochi minuti; i
              dispositivi riconosciuti dopo 30 giorni.
            </p>
          </Section>

          <Section id="sicurezza" title="7. Sicurezza">
            <p>
              Le password sono conservate solo in forma cifrata, il traffico viaggia su connessione
              protetta e l’accesso da un dispositivo nuovo richiede un codice di verifica inviato via
              email. L’accesso ai dati è limitato ai ruoli che ne hanno bisogno.
            </p>
          </Section>

          <Section id="diritti" title="8. I tuoi diritti">
            <p>
              Puoi chiedere accesso, rettifica, cancellazione o portabilità dei tuoi dati, limitarne o
              opporti al trattamento e revocare i consensi prestati, scrivendo a{' '}
              <a href={`mailto:${CONTACT}`} className="font-medium text-primary hover:underline">{CONTACT}</a>.
              Hai inoltre diritto di proporre reclamo all’autorità di controllo competente.
            </p>
          </Section>

          <Section id="modifiche" title="9. Modifiche">
            <p>
              Se questa informativa cambia, pubblichiamo la versione aggiornata su questa pagina
              modificando la data in alto; nei casi rilevanti ti avvisiamo anche via email.
            </p>
          </Section>
        </div>

        <footer className="mt-16 border-t border-border/60 pt-8 text-sm text-muted-foreground" data-testid="privacy-footer">
          © {new Date().getFullYear()} {brand} · {OWNER} · {CONTACT}
        </footer>
      </main>
    </div>
  );
};

export default PrivacyPage;
